import { G } from '@mobily/ts-belt';
import LockIcon from '@mui/icons-material/Lock';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Step,
  StepLabel,
  Stepper,
  SxProps,
} from '@mui/material';
import { ReactElement, ReactNode, useMemo, useState } from 'react';
import {
  // permissions check not needed
  // eslint-disable-next-line @typescript-eslint/no-restricted-imports
  Button,
  DataProvider,
  FormProps,
  Identifier,
  useDataProvider,
  useLoading,
  useNotify,
  useSaveContext,
  useTranslate,
} from 'react-admin';
import { FieldValues, UseFormReturn, useFormContext } from 'react-hook-form';

import { PermissionActionT } from '@Common/types/bo/authorization';

import { DialogTitleWithX, SaveButton } from '@sh4res/bo-common/components';
import { ActionModalFormWithToolbar } from '@sh4res/bo-common/components/ActionModalForm';
import { DialogToolbar } from '@sh4res/bo-common/components/DialogToolbar/DialogToolbar';
import {
  BackButton,
  NextButton,
  SaveDraftButton,
  useSubmitGuard,
  validateForm,
} from '@sh4res/bo-common/components/StepperFormButtons';
import { ResourceBasePath, useResourceBasePath } from '@sh4res/bo-common/hooks';
import { useDeepMemo } from '@sh4res/bo-common/hooks/useDeepMemo';
import { Resources } from '@sh4res/bo-common/utils';

export type ExtraValidationResult = { isValid: true } | { isValid: false; message: string | null };

export type ValidateFunc = (
  values: UseFormReturn<FieldValues>,
  dataProvider: DataProvider,
  resourceBasePath: ResourceBasePath,
) => Promise<ExtraValidationResult> | ExtraValidationResult;

export type ConfirmationModalDetails =
  | { shouldConfirm: false }
  | {
      blocking: boolean;
      title: ReactElement;
      body: ReactElement;
      shouldConfirm: true;
    };

export interface StepperFormStep {
  label: string;
  content: ReactElement;
  locked?: ((values: FieldValues) => boolean) | boolean;
  hidden?: ((values: FieldValues) => boolean) | boolean;
  hideDraft?: ((values: FieldValues) => boolean) | boolean;
  draftDisabled?: ((values: FieldValues) => boolean) | boolean;
  nextDisabled?: ((values: FieldValues) => boolean) | boolean;
  saveDisabled?: ((values: FieldValues) => boolean) | boolean;
  validate?: ValidateFunc;
  customSave?: {
    transform: (values: FieldValues) => FieldValues;
    label: string;
    onSuccess?: (id: Identifier, data: FieldValues) => void | Promise<void>;
    onError?: (error: string) => void;
  };
  confirmation?: (
    values: FieldValues,
    translate: ReturnType<typeof useTranslate>,
  ) => ConfirmationModalDetails;
}

export interface StepperFormStepFinal extends StepperFormStep {
  locked: boolean;
  hideDraft: boolean;
  draftDisabled: boolean;
  nextDisabled: boolean;
  saveDisabled: boolean;
  prevStep: number | null;
  nextStep: number | null;
  index: number;
  isFinalStep: boolean;
}
export interface StepperFormButtonProps {
  setActiveStep: (newStep: number) => void;
  step: StepperFormStepFinal;
  validate?: ValidateFunc;
  onBack?: (currentStep: number, context: UseFormReturn<FieldValues>) => void;
  isLoading?: boolean;
  'data-testid'?: string;
}

export type ResourceDraft = {
  transform: (record: FieldValues) => Promise<FieldValues>;
  resource: Resources;
} & ({ action: 'update'; resourceId: number } | { action: 'create' });
export interface StepperFormProps {
  resetOptions?: FormProps['resetOptions'];
  steps: StepperFormStep[];
  defaultValues?: FieldValues;
  permissions: {
    resource: Resources;
    action: PermissionActionT;
  };
  children?: ReactNode;
  alwaysEnableSaveButton?: boolean;
  /** Extra toolbar buttons, shown on every step before the navigation buttons (e.g. a delete button) */
  toolbarExtras?: ReactNode;
  onBack?: (currentStep: number, context: UseFormReturn<FieldValues>) => void;
  onSave?: (context: UseFormReturn<FieldValues>) => Promise<void>;
  sx?: SxProps;
  draft?: ResourceDraft;
  testId?: string;
}

const evaluatePredicate = (
  predicate: ((values: FieldValues) => boolean) | boolean | undefined,
  values: FieldValues,
): boolean => (typeof predicate === 'function' ? predicate(values) : !!predicate);

export const getUnlockedStepLinks = (
  unlockedIndices: number[],
  index: number,
): { prevStep: number | null; nextStep: number | null } => {
  const pos = unlockedIndices.indexOf(index);

  return {
    prevStep: pos > 0 ? (unlockedIndices.at(pos - 1) ?? null) : null,
    nextStep: unlockedIndices.at(pos + 1) ?? null,
  };
};

const getInitialStep = (steps: StepperFormStep[], values: FieldValues): number => {
  const index = steps.findIndex(
    (step) => !evaluatePredicate(step.hidden, values) && !evaluatePredicate(step.locked, values),
  );

  return index === -1 ? 0 : index;
};

const useEvaluatedSteps = (activeStepIdx: number, steps: StepperFormStep[]) => {
  const { watch } = useFormContext();
  const formValues = watch();

  const {
    realActiveStep,
    finalSteps,
  }: { realActiveStep: number; finalSteps: StepperFormStepFinal[] } = useDeepMemo(() => {
    const evaluatedSteps = steps.map((step) => ({
      ...step,
      locked: evaluatePredicate(step.locked, formValues),
      hidden: evaluatePredicate(step.hidden, formValues),
      hideDraft: evaluatePredicate(step.hideDraft, formValues),
      draftDisabled: evaluatePredicate(step.draftDisabled, formValues),
      nextDisabled: evaluatePredicate(step.nextDisabled, formValues),
      saveDisabled: evaluatePredicate(step.saveDisabled, formValues),
    }));

    const unlockedIndices = evaluatedSteps
      .map((step, index) => (step.locked || step.hidden ? null : index))
      .filter((idx): idx is number => idx !== null);

    const hiddenIndices = evaluatedSteps
      .map((step, index) => (step.hidden ? null : index))
      .filter((idx): idx is number => idx !== null);

    const realActiveStep = hiddenIndices.indexOf(activeStepIdx);

    return {
      realActiveStep,
      finalSteps: evaluatedSteps.reduce((reducedSteps: StepperFormStepFinal[], step, index) => {
        if (step.hidden) {
          return reducedSteps;
        }
        if (step.locked) {
          reducedSteps.push({ ...step, prevStep: null, nextStep: null, index, isFinalStep: false });
          return reducedSteps;
        }

        const { prevStep, nextStep } = getUnlockedStepLinks(unlockedIndices, index);

        reducedSteps.push({
          ...step,
          prevStep,
          nextStep,
          index,
          isFinalStep: nextStep === null,
        });

        return reducedSteps;
      }, []),
    };
  }, [steps, formValues, activeStepIdx]);

  return useMemo(
    () => ({
      finalSteps,
      realActiveStep,
      step: finalSteps[realActiveStep],
    }),
    [finalSteps, realActiveStep],
  );
};

type ConfirmationModalState =
  | (ConfirmationModalDetails & {
      onConfirmation: () => void | Promise<void>;
    })
  | null;

const StepperNavigation = ({
  steps,
  activeStep,
  setActiveStep,
  permissions,
  alwaysEnableSaveButton,
  draft,
  toolbarExtras,
  onBack,
  onSave,
  testId,
}: StepperFormProps & { activeStep: number; setActiveStep: (step: number) => void }) => {
  const { step } = useEvaluatedSteps(activeStep, steps);
  const isLastStep = step.nextStep === null;
  const isFirstStep = step.prevStep === null;
  const isLoading = useLoading();
  const formContext = useFormContext();
  const { save } = useSaveContext();
  const translate = useTranslate();
  const notify = useNotify();
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
    useState<ConfirmationModalState>(null);
  const dataProvider = useDataProvider();
  const resourceBasePath = useResourceBasePath();
  const submitGuard = useSubmitGuard();
  const { isSubmitting, runExclusive } = submitGuard;

  return (
    <DialogToolbar
      hideCancel
      button={
        <>
          {toolbarExtras}
          {!isFirstStep && (
            <BackButton
              setActiveStep={setActiveStep}
              step={step}
              onBack={onBack}
              data-testid={testId ? `${testId}_BackButton` : undefined}
            />
          )}
          {!step.hideDraft && (
            <SaveDraftButton
              draft={draft}
              permissions={permissions}
              isLoading={isLoading}
              draftDisabled={step.draftDisabled}
              submitGuard={submitGuard}
              validate={step.validate}
              data-testid={testId ? `${testId}_SaveDraftButton` : undefined}
            />
          )}
          {isLastStep && step.customSave && (
            <SaveDraftButton
              draft={draft}
              permissions={permissions}
              customSave={step.customSave}
              isLoading={isLoading}
              draftDisabled={step.saveDisabled}
              submitGuard={submitGuard}
              validate={step.validate}
              data-testid={testId ? `${testId}_CustomSaveDraftButton` : undefined}
            />
          )}
          {isLastStep && !step.customSave && step.isFinalStep && (
            <SaveButton
              color="primary"
              permissions={permissions}
              // react-admin ignores `disabled` when `alwaysEnable` is set
              alwaysEnable={alwaysEnableSaveButton && !step.saveDisabled && !isSubmitting}
              disabled={isLoading || isSubmitting || step.saveDisabled}
              testId={testId ? `${testId}_SaveButton` : undefined}
              onClick={async (event) => {
                const handleDefaultSave = async () => {
                  const isFormValid = await validateForm(
                    formContext,
                    notify,
                    dataProvider,
                    resourceBasePath,
                    steps[activeStep].validate,
                  );

                  if (!isFormValid) {
                    return;
                  }

                  // awaited so the submit guard is held until the request completes
                  if (save) {
                    await formContext.handleSubmit(async (values) => save(values))();
                  }
                };

                // always prevent the native submit: saving is fully handled here
                event.preventDefault();

                const confirmation = step.confirmation?.(formContext.getValues(), translate);
                if (confirmation?.shouldConfirm) {
                  setIsConfirmationModalOpen({
                    ...confirmation,
                    onConfirmation: async () =>
                      runExclusive(async () => {
                        if (onSave !== undefined) {
                          await onSave(formContext);
                        } else {
                          await handleDefaultSave();
                        }
                      }),
                  });
                  return;
                }

                await runExclusive(async () => {
                  if (onSave !== undefined) {
                    await onSave(formContext);
                  } else {
                    await handleDefaultSave();
                  }
                });
              }}
            />
          )}
          {!isLastStep && (
            <NextButton
              data-testid={testId ? `${testId}_NextButton` : undefined}
              onClick={async () => {
                const isFormValid = await validateForm(
                  formContext,
                  notify,
                  dataProvider,
                  resourceBasePath,
                  steps[activeStep].validate,
                );
                const { nextStep } = step;
                if (!isFormValid || G.isNullable(nextStep)) {
                  return;
                }

                const confirmation = step.confirmation?.(formContext.getValues(), translate);
                if (confirmation?.shouldConfirm) {
                  setIsConfirmationModalOpen({
                    ...confirmation,
                    onConfirmation: () => setActiveStep(nextStep),
                  });
                  return;
                }
                setActiveStep(nextStep);
              }}
              step={step}
              isLoading={isLoading}
            />
          )}
          <Dialog
            open={!!isConfirmationModalOpen}
            onClose={() => setIsConfirmationModalOpen(null)}
            maxWidth="sm"
            fullWidth>
            {isConfirmationModalOpen && isConfirmationModalOpen.shouldConfirm && (
              <>
                <DialogTitleWithX onClose={() => setIsConfirmationModalOpen(null)}>
                  {isConfirmationModalOpen.title}
                </DialogTitleWithX>
                <DialogContent>{isConfirmationModalOpen.body}</DialogContent>
                <DialogActions>
                  <Button
                    variant="text"
                    data-testid={testId ? `${testId}_ConfirmationCancelButton` : undefined}
                    onClick={() => {
                      setIsConfirmationModalOpen(null);
                    }}
                    label="ra.action.back"
                  />
                  {!isConfirmationModalOpen.blocking && (
                    <Button
                      variant="contained"
                      data-testid={testId ? `${testId}_ConfirmationConfirmButton` : undefined}
                      onClick={() => {
                        isConfirmationModalOpen.onConfirmation();
                        setIsConfirmationModalOpen(null);
                      }}
                      label="ra.action.confirm"
                    />
                  )}
                </DialogActions>
              </>
            )}
          </Dialog>
        </>
      }
    />
  );
};

const StepperSidebar = ({
  steps,
  activeStep,
}: {
  steps: StepperFormStep[];
  activeStep: number;
}) => {
  const { finalSteps, realActiveStep } = useEvaluatedSteps(activeStep, steps);
  const translate = useTranslate();

  return useMemo(
    () => (
      <Stepper
        activeStep={realActiveStep}
        orientation="vertical"
        component="ul"
        sx={{
          height: 'fit-content',
          position: 'sticky',
          margin: 0,
          padding: 0,
          listStyle: 'none',
          marginLeft: { xs: 0, md: '20px' },
          top: '80px',
          '.MuiStepLabel-label': { fontSize: '1.1rem', fontWeight: '600 !important' },
          '.MuiStepIcon-root': { width: 32, height: 32, color: 'whitesmoke' },
          '.MuiStepIcon-text': { fill: 'darkgray' },
          '.MuiStepIcon-root.Mui-active .MuiStepIcon-text': { fill: 'white' },
          '.MuiStepConnector-root': { marginLeft: '15px' },
        }}
        connector={
          <Box
            component="li"
            sx={{
              marginLeft: '15px',
              minHeight: '24px',
              borderLeft: '1px solid rgb(189, 189, 189)',
            }}
          />
        }>
        {finalSteps.map((step) => (
          <Step key={step.index} component="li">
            <StepLabel
              aria-current={realActiveStep === step.index ? 'step' : undefined}
              aria-label={translate('resources.bo_common.accessibility.stepper.ariaLabel', {
                label: translate(step.label),
                current: step.index + 1,
                total: finalSteps.length,
                suffix:
                  realActiveStep === step.index
                    ? translate('resources.bo_common.accessibility.stepper.currentSuffix')
                    : '',
              })}
              icon={
                step.locked ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    }}>
                    <LockIcon sx={{ color: 'grey.500' }} fontSize="small" />
                  </Box>
                ) : undefined
              }>
              {translate(step.label)}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    ),
    [finalSteps, realActiveStep, translate],
  );
};

export const StepperForm = ({
  steps,
  defaultValues,
  permissions,
  children,
  alwaysEnableSaveButton,
  sx,
  draft,
  toolbarExtras,
  onBack,
  onSave,
  resetOptions,
  testId,
}: StepperFormProps) => {
  const [activeStep, setActiveStep] = useState(() => getInitialStep(steps, defaultValues ?? {}));

  return (
    <ActionModalFormWithToolbar
      defaultValues={defaultValues}
      sx={sx}
      toolbar={
        <StepperNavigation
          steps={steps}
          onBack={onBack}
          onSave={onSave}
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          permissions={permissions}
          alwaysEnableSaveButton={alwaysEnableSaveButton}
          draft={draft}
          toolbarExtras={toolbarExtras}
          testId={testId}
        />
      }
      resetOptions={resetOptions}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(200px, 240px) minmax(0, 1fr)' },
          width: '100%',
          height: '100%',
          minWidth: 0,
          columnGap: 2,
        }}>
        <Box sx={{ minWidth: 0, width: '100%' }}>
          <StepperSidebar steps={steps} activeStep={activeStep} />
        </Box>
        <Box sx={{ height: '100%', minWidth: 0, width: '100%' }}>{steps[activeStep].content}</Box>
        {children}
      </Box>
    </ActionModalFormWithToolbar>
  );
};
